// Flooring support: pure geometry and classification for the tiled/screed floor
// finishes drawn on the layer below the rooms. DOM-free so `node --test` can
// import it directly (see esbuild.test.mjs); every rule here is unit-tested.
import type {
  Flooring,
  FloorKind,
  Vec2,
} from '../shared/types';
import {
  distance,
  pointInPolygon,
  projectToSegment,
} from './geometry';

/** The four floor finishes, in toolbar / schedule order. */
export const FLOOR_KINDS: FloorKind[] = ['tiled-flat', 'tiled-slope', 'screed-flat', 'screed-slope'];

/** A sloping floor falls to a drain; a flat one has none. */
export function floorIsSloping(k: FloorKind): boolean {
  return k === 'tiled-slope' || k === 'screed-slope';
}

/** The finish material — its hatch pattern is keyed off this. */
export function floorMaterial(k: FloorKind): 'tiled' | 'screed' {
  return k === 'tiled-flat' || k === 'tiled-slope' ? 'tiled' : 'screed';
}

/** English label (the DICT key the panel and schedule translate through). */
export function floorLabel(k: FloorKind): string {
  switch (k) {
    case 'tiled-flat': return 'Flat tiled floor';
    case 'tiled-slope': return 'Sloping tiled floor';
    case 'screed-flat': return 'Flat screed';
    case 'screed-slope': return 'Sloping screed';
  }
}

/** Signed polygon area (mm²); positive for CCW winding. */
export function polygonSignedArea(pts: Vec2[]): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) a += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
  return a / 2;
}

/** Absolute polygon area (mm²). */
export function polygonArea(pts: Vec2[]): number {
  return Math.abs(polygonSignedArea(pts));
}

/** Area-weighted polygon centroid; falls back to the vertex average for a
 *  degenerate (zero-area) ring. */
export function polygonCentroid(pts: Vec2[]): Vec2 {
  if (pts.length === 0) return { x: 0, y: 0 };
  let cx = 0, cy = 0, a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const cross = pts[j].x * pts[i].y - pts[i].x * pts[j].y;
    cx += (pts[j].x + pts[i].x) * cross;
    cy += (pts[j].y + pts[i].y) * cross;
    a += cross;
  }
  if (Math.abs(a) < 1e-9) {
    const s = pts.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    return { x: s.x / pts.length, y: s.y / pts.length };
  }
  a *= 0.5;
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

/** Even-odd ray cast: is `p` inside the polygon? */

/** The nearest point ON the polygon boundary to `p` (its closest edge foot). */
export function nearestBoundaryPoint(p: Vec2, pts: Vec2[]): Vec2 {
  let best = pts[0] ?? p, bestD = Infinity;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const foot = projectToSegment(p, pts[j], pts[i]);
    const d = distance(p, foot);
    if (d < bestD) { bestD = d; best = foot; }
  }
  return best;
}

/** Confine a point to the polygon: unchanged when inside, else pulled to the
 *  nearest boundary point (with a tiny inward nudge so it reads as inside). */
export function confineToPolygon(p: Vec2, pts: Vec2[]): Vec2 {
  if (pts.length < 3 || pointInPolygon(p, pts)) return p;
  const b = nearestBoundaryPoint(p, pts);
  const c = polygonCentroid(pts);
  const dx = c.x - b.x, dy = c.y - b.y, len = Math.hypot(dx, dy) || 1;
  const nudge = Math.min(1, len); // 1 mm toward the centroid, clamped
  return { x: b.x + (dx / len) * nudge, y: b.y + (dy / len) * nudge };
}

/** Perpendicular dimension from `p` to every edge of the polygon (its foot on the
 *  segment and the distance) — the temporary figures shown while a drain drags. */
export function edgeDistances(p: Vec2, pts: Vec2[]): Array<{ from: Vec2; to: Vec2; len: number }> {
  const out: Array<{ from: Vec2; to: Vec2; len: number }> = [];
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const foot = projectToSegment(p, pts[j], pts[i]);
    out.push({ from: { ...p }, to: foot, len: distance(p, foot) });
  }
  return out;
}

/** The drain's centre for a floor, or null when it has none. */
export function floorDrainCentre(f: Flooring): Vec2 | null {
  return f.drain ? { x: f.drain.x, y: f.drain.y } : null;
}

/** A fall arrow's sizes: its `shaft`, its tip's `gap` outside the drain's rim,
 *  the rim's radius when the drain's part is unknown (`rim`), and the `room` an
 *  edge needs beyond them to hold one. */
export interface FallArrowSize {
  shaft: number;
  gap: number;
  rim: number;
  room: number;
}
/** On the canvas, in screen px. */
export const FALL_ARROW_PX: FallArrowSize = { shaft: 16, gap: 4, rim: 8, room: 4 };
/** In a DXF, in mm: the canvas's px at about 14 mm each — the ceiling's fall
 *  arrows' 28 px → 400 mm (`CEILING_ARROW_MM`), a 1:50 print's scale, the one
 *  the DXF's text sizes assume. */
export const FALL_ARROW_MM: FallArrowSize = { shaft: 225, gap: 55, rim: 110, room: 55 };

/** A sloping floor's fall arrows: one from the middle of each edge of `ring`
 *  toward the drain, its tip `gap` outside the drain's rim (radius `r`, else
 *  `size.rim`), `shaft` long; none from an edge too close to the drain to hold
 *  one. In whatever units the points and sizes share: the canvas passes screen
 *  points and `FALL_ARROW_PX`, the DXF world mm and `FALL_ARROW_MM`. */
export function fallArrows(ring: readonly Vec2[], drain: Vec2, r: number | null, size: FallArrowSize): Array<{ tail: Vec2; tip: Vec2 }> {
  const rim = r ?? size.rim, out: Array<{ tail: Vec2; tip: Vec2 }> = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const dx = drain.x - (a.x + b.x) / 2, dy = drain.y - (a.y + b.y) / 2, len = Math.hypot(dx, dy);
    if (len < rim + size.gap + size.shaft + size.room) continue;
    const ux = dx / len, uy = dy / len;
    const tip = { x: drain.x - ux * (rim + size.gap), y: drain.y - uy * (rim + size.gap) };
    out.push({ tail: { x: tip.x - ux * size.shaft, y: tip.y - uy * size.shaft }, tip });
  }
  return out;
}
